import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ReactComponent as MessageChatSquare } from "../../assets/icons/message-chat-square.svg";
import {
  AccessRequestService,
  buildAiAnalystRequestDescription,
  isAiAnalystRequest,
} from "../../services/accessRequestService";
import { Skeleton, SkeletonText } from "../../components/Skeleton";
import { CHAT_INPUT_MEASURE } from "../../components/AiChatComponent/panelComponents/AiChatInput";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";
import { OrgChatAccessCheck } from "../../utils/termsAuditTrail";

// Must outrank everything the page paints, including the peek rail's toggle at z-[120] — otherwise that one icon punches through the cover. Still far below the modal layer (10050).
const OVERLAY_Z = "z-[200]";

interface OrgChatAccessOverlayProps {
  access: OrgChatAccessCheck;
}

/** Blurred overlay shown on top of the AI Analyst page for org users whose organization doesn't have chat access enabled. Both owners and members can request access; the Erode team reviews requests on the admin AI Analyst Access page and enables access there. */
const OrgChatAccessOverlay: React.FC<OrgChatAccessOverlayProps> = ({ access }) => {
  const user = useSelector((state: any) => state.user);
  const { capture } = usePostHogCapture();

  const email = user?.details?.email;
  const userName = user?.permissionDetails?.name;

  const [checkingExisting, setCheckingExisting] = useState(true);
  const [requested, setRequested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If this user already has a pending AI Analyst request, show the "sent" state
  useEffect(() => {
    let cancelled = false;
    if (!email || access.status !== "blocked") {
      setCheckingExisting(false);
      return;
    }
    AccessRequestService.getAccessRequestsByEmail(email)
      .then((requests) => {
        if (cancelled) return;
        const pending = requests.some(
          (request) => isAiAnalystRequest(request) && request.status === "PENDING"
        );
        setRequested(pending);
      })
      .finally(() => {
        if (!cancelled) setCheckingExisting(false);
      });
    return () => { cancelled = true; };
  }, [email, access.status]);

  const handleRequestAccess = async () => {
    if (!email || !access.organizationId || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      // Re-check right before creating.
      const existing = await AccessRequestService.getAccessRequestsByEmail(email);
      const alreadyPending = existing.some(
        (request) => isAiAnalystRequest(request) && request.status === "PENDING"
      );
      if (alreadyPending) {
        setRequested(true);
        return;
      }

      await AccessRequestService.createAccessRequest({
        email,
        description: buildAiAnalystRequestDescription({
          organizationId: access.organizationId,
          organizationName: access.organizationName,
          userName,
          email,
        }),
      });

      capture(POSTHOG_EVENTS.ORG_CHAT_ACCESS_REQUESTED, {
        organization_id: access.organizationId,
      });

      setRequested(true);
    } catch (err) {
      console.error("Error creating AI Analyst access request:", err);
      setError("Something went wrong sending your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    // The overlay only mounts once the viewer is confirmed "blocked"; here we're just waiting to see whether they already have a pending request.
    if (checkingExisting) {
      return <div className="text-eai-ink-faint py-4 text-center">Loading...</div>;
    }

    if (requested) {
      return (
        <p className="text-sm text-eai-ink-muted leading-relaxed text-center">
          Your request has been sent — we'll be in contact with you shortly.
        </p>
      );
    }

    return (
      <>
        <p className="text-sm text-eai-ink-muted leading-relaxed text-center">
          You don't have access to the AI Analyst yet. Request access and we'll
          be in contact with you shortly.
        </p>
        {error && <p className="text-eai-danger-text text-xs mt-2 text-center">{error}</p>}
        <button
          type="button"
          onClick={handleRequestAccess}
          disabled={submitting}
          className="eai-btn-cta mt-6"
        >
          {submitting ? "Sending..." : "Request Access"}
        </button>
      </>
    );
  };

  if (access.status === "loading") {
    // Access hasn't resolved yet. Cover the page with a placeholder of the chat itself — NOT the access-required blur treatment — so approved users never flash the gating overlay, and a genuinely blocked user never sees the chat before gating kicks in.
    return (
      <div
        role="status"
        aria-label="Loading AI Analyst"
        className={`absolute inset-0 ${OVERLAY_Z} flex flex-col gap-4 bg-eai-surface p-5`}
      >
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-eai-control-sm w-eai-control-sm" radius="full" />
          <Skeleton className="h-2.5 w-28" />
        </div>
        <div className="flex min-h-0 flex-1 flex-col justify-end gap-4">
          <SkeletonText lines={2} className="max-w-[70%]" />
          <SkeletonText lines={3} className="ml-auto max-w-[60%]" />
          <SkeletonText lines={2} className="max-w-[75%]" />
        </div>
        <Skeleton className={`mx-auto h-[102px] w-full ${CHAT_INPUT_MEASURE}`} radius="lg" />
      </div>
    );
  }

  return (
    <div className={`absolute inset-0 ${OVERLAY_Z} flex items-center justify-center bg-eai-surface-card/20 p-5 backdrop-blur-sm`}>
      <div className="eai-card flex w-full max-w-md flex-col items-center p-8">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-eai-card border border-eai-border">
          <MessageChatSquare className="w-6 h-6" />
        </div>
        <div className="eai-h3-title mb-2 text-center">
          AI Analyst access required
        </div>
        {renderBody()}
      </div>
    </div>
  );
};

export default OrgChatAccessOverlay;
